import {
  AfterViewInit,
  Directive,
  ElementRef,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';

import { isGooglePlacesLoaded, loadGoogleMapScript } from 'app/_utils/helpers';
import { parseGooglePlaceAddress } from 'app/_utils/places-address';

declare var google: any;

/**
 * Address lookup for street-line inputs.
 *
 * Uses Places predictions and place details, and draws its own list.
 * The Maps Autocomplete widget is not used: once a lookup starts it sets
 * the host input to disabled, which freezes Address 1 inside modals.
 *
 * Usage (object / ContactAddress / vendor / etc.):
 *   <input [(ngModel)]="model.address1" [appPlacesAddress]="model">
 *
 * Usage (FormGroup):
 *   <input formControlName="address1" [appPlacesAddress]="form">
 */
@Directive({
  selector: '[appPlacesAddress]',
})
export class PlacesAddressDirective implements AfterViewInit, OnChanges, OnDestroy {
  @Input('appPlacesAddress') model: any;
  @Input() appPlacesAddressStreet = 'address1';
  @Input() appPlacesAddressCity = 'city';
  @Input() appPlacesAddressState = 'state';
  @Input() appPlacesAddressZip = 'zip';
  @Input() appPlacesAddressCountry = 'country';
  /** Pass null/'' to allow all countries. Default US to match Omni state lists. */
  @Input() appPlacesAddressCountryRestriction: string | string[] | null = 'us';

  private autocompleteService: any;
  private placesService: any;
  private sessionToken: any;
  private panel: HTMLDivElement | null = null;
  private predictions: any[] = [];
  private activeIndex = -1;
  private timer: any;
  private requestSeq = 0;
  private destroyed = false;
  private started = false;
  private applying = false;
  private placesErrorShown = false;

  private readonly onInput = () => this.scheduleFetch();
  private readonly onKeydown = (event: KeyboardEvent) => this.handleKeydown(event);
  private readonly onReposition = () => this.positionPanel();
  private readonly onDocPointer = (event: Event) => {
    const target = event.target as Node | null;
    const el = this.elRef.nativeElement;
    if (!target || target === el || this.panel?.contains(target)) return;
    this.hidePanel();
  };

  constructor(
    private readonly elRef: ElementRef<HTMLInputElement>,
    private readonly zone: NgZone,
  ) {}

  ngAfterViewInit() {
    this.ensureStarted();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['model'] && this.model) {
      this.ensureStarted();
    }
  }

  ngOnDestroy() {
    this.destroyed = true;
    this.requestSeq += 1;
    clearTimeout(this.timer);
    this.teardown();
  }

  private ensureStarted() {
    if (this.destroyed || this.started || !this.model) return;
    const el = this.elRef.nativeElement;
    if (!el || el.disabled || el.readOnly) return;
    loadGoogleMapScript((loaded) => {
      if (this.destroyed || this.started) return;
      if (!loaded || !isGooglePlacesLoaded()) return;
      this.started = true;
      this.bind();
    });
  }

  private bind() {
    const el = this.elRef.nativeElement;
    const places = google.maps.places;
    try {
      if (places.AutocompleteService) {
        this.autocompleteService = new places.AutocompleteService();
      }
      if (places.PlacesService) {
        this.placesService = new places.PlacesService(document.createElement('div'));
      }
      if (places.AutocompleteSessionToken) {
        this.sessionToken = new places.AutocompleteSessionToken();
      }
    } catch (err) {
      this.notePlacesError(err);
    }

    this.zone.runOutsideAngular(() => {
      el.addEventListener('input', this.onInput);
      el.addEventListener('keydown', this.onKeydown);
      document.addEventListener('pointerdown', this.onDocPointer, true);
      window.addEventListener('scroll', this.onReposition, true);
      window.addEventListener('resize', this.onReposition);
    });
  }

  private teardown() {
    const el = this.elRef.nativeElement;
    el?.removeEventListener('input', this.onInput);
    el?.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener('pointerdown', this.onDocPointer, true);
    window.removeEventListener('scroll', this.onReposition, true);
    window.removeEventListener('resize', this.onReposition);
    this.panel?.remove();
    this.panel = null;
  }

  private scheduleFetch() {
    if (this.applying) return;
    clearTimeout(this.timer);
    const value = (this.elRef.nativeElement.value || '').trim();
    if (value.length < 2) {
      this.hidePanel();
      return;
    }
    this.timer = setTimeout(() => this.fetchPredictions(value), 200);
  }

  private fetchPredictions(input: string) {
    const seq = ++this.requestSeq;
    this.loadSuggestions(input).then((suggestions) => {
      if (this.destroyed || seq !== this.requestSeq) return;
      this.zone.run(() => {
        this.predictions = suggestions.slice(0, 5);
        this.activeIndex = this.predictions.length ? 0 : -1;
        if (!this.predictions.length) {
          this.hidePanel();
          return;
        }
        this.renderPanel();
      });
    }).catch((err) => {
      if (this.destroyed || seq !== this.requestSeq) return;
      this.notePlacesError(err);
      this.zone.run(() => this.hidePanel());
    });
  }

  private notePlacesError(err: unknown) {
    if (this.placesErrorShown) return;
    this.placesErrorShown = true;
    const message = err instanceof Error ? err.message : String(err || '');
    if (message) {
      console.warn('Address lookup failed:', message);
    }
  }

  private regionCodes(): string[] | undefined {
    const restriction = this.appPlacesAddressCountryRestriction;
    if (!restriction) return undefined;
    const codes = Array.isArray(restriction) ? restriction : [restriction];
    return codes.map((code) => String(code).toLowerCase());
  }

  private async fetchNewSuggestions(places: any, input: string): Promise<any[]> {
    const request: any = {
      input,
      sessionToken: this.sessionToken,
      includedPrimaryTypes: ['premise', 'subpremise', 'street_address', 'route'],
    };
    const regions = this.regionCodes();
    if (regions) request.includedRegionCodes = regions;
    let { suggestions } = await places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
    if (!suggestions?.length && request.includedPrimaryTypes) {
      delete request.includedPrimaryTypes;
      ({ suggestions } = await places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request));
    }
    return (suggestions || []).map((suggestion: any) => {
      const prediction = suggestion.placePrediction;
      if (!prediction) return null;
      const text = prediction.text;
      return {
        description: typeof text?.toString === 'function' ? text.toString() : String(text || ''),
        prediction,
      };
    }).filter(Boolean);
  }

  private async loadSuggestions(input: string): Promise<any[]> {
    const places = google.maps.places;
    if (places.AutocompleteSuggestion?.fetchAutocompleteSuggestions) {
      try {
        return await this.fetchNewSuggestions(places, input);
      } catch (err) {
        this.notePlacesError(err);
      }
    }

    if (!this.autocompleteService) return [];
    const request: any = { input, sessionToken: this.sessionToken };
    if (this.appPlacesAddressCountryRestriction) {
      request.componentRestrictions = { country: this.appPlacesAddressCountryRestriction };
    }
    return new Promise((resolve) => {
      this.autocompleteService.getPlacePredictions(request, (results: any[], status: string) => {
        if (status !== 'OK' || !Array.isArray(results)) {
          if (status && status !== 'ZERO_RESULTS') {
            this.notePlacesError(new Error(status));
          }
          resolve([]);
          return;
        }
        resolve(results.map((result) => ({
          description: result.description,
          legacy: result,
        })));
      });
    });
  }

  private renderPanel() {
    if (!this.panel) {
      this.panel = document.createElement('div');
      this.panel.className = 'omni-places-panel';
      this.panel.setAttribute('role', 'listbox');
      document.body.appendChild(this.panel);
    }
    this.panel.replaceChildren();
    this.predictions.forEach((prediction, index) => {
      const option = document.createElement('button');
      option.type = 'button';
      option.className = 'omni-places-option';
      if (index === this.activeIndex) option.classList.add('is-active');
      option.setAttribute('role', 'option');
      option.textContent = prediction.description || '';
      option.addEventListener('mousedown', (event) => {
        event.preventDefault();
        this.choose(index);
      });
      this.panel.appendChild(option);
    });
    this.positionPanel();
  }

  private positionPanel() {
    if (!this.panel) return;
    const rect = this.elRef.nativeElement.getBoundingClientRect();
    this.panel.style.left = `${rect.left}px`;
    this.panel.style.top = `${rect.bottom + 2}px`;
    this.panel.style.width = `${rect.width}px`;
  }

  private hidePanel() {
    this.predictions = [];
    this.activeIndex = -1;
    this.panel?.remove();
    this.panel = null;
  }

  private handleKeydown(event: KeyboardEvent) {
    if (!this.predictions.length) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.activeIndex = (this.activeIndex + 1) % this.predictions.length;
      this.zone.run(() => this.renderPanel());
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.activeIndex = (this.activeIndex - 1 + this.predictions.length) % this.predictions.length;
      this.zone.run(() => this.renderPanel());
    } else if (event.key === 'Enter' && this.activeIndex >= 0) {
      event.preventDefault();
      this.zone.run(() => this.choose(this.activeIndex));
    } else if (event.key === 'Escape') {
      this.zone.run(() => this.hidePanel());
    }
  }

  private choose(index: number) {
    const prediction = this.predictions[index];
    if (!prediction) return;
    const token = this.sessionToken;
    this.hidePanel();
    const Token = google.maps.places.AutocompleteSessionToken;
    this.sessionToken = Token ? new Token() : undefined;
    if (prediction.prediction?.toPlace) {
      const place = prediction.prediction.toPlace();
      place.fetchFields({
        fields: ['addressComponents', 'location', 'displayName'],
      }).then(() => {
        if (this.destroyed) return;
        this.zone.run(() => this.applyPlace(this.toLegacyPlace(place)));
      }).catch(() => undefined);
    } else if (prediction.legacy?.place_id && this.placesService) {
      this.placesService.getDetails({
        placeId: prediction.legacy.place_id,
        fields: ['address_components', 'name', 'geometry'],
        sessionToken: token,
      }, (place: any, status: string) => {
        if (this.destroyed || status !== 'OK' || !place) return;
        this.zone.run(() => this.applyPlace(place));
      });
    }
  }

  private toLegacyPlace(place: any) {
    const components = place?.addressComponents || place?.address_components || [];
    const displayName = place?.displayName;
    return {
      name: displayName?.text || displayName || place?.name,
      address_components: components.map((component: any) => ({
        long_name: component.longText ?? component.long_name ?? '',
        short_name: component.shortText ?? component.short_name ?? '',
        types: component.types || [],
      })),
      geometry: place?.location ? { location: place.location } : place?.geometry,
    };
  }

  private applyPlace(place: any) {
    if (!this.model) return;
    const parsed = parseGooglePlaceAddress(place?.address_components);
    if (!parsed.address1 && place?.name) {
      parsed.address1 = place.name;
    }
    if (!parsed.address1 && !parsed.city && !parsed.state && !parsed.zip) {
      return;
    }

    this.setField(this.appPlacesAddressStreet, parsed.address1);
    this.setField(this.appPlacesAddressCity, parsed.city);
    this.setField(this.appPlacesAddressState, parsed.state);
    this.setField(this.appPlacesAddressZip, parsed.zip);
    if (parsed.country) {
      this.setField(this.appPlacesAddressCountry, parsed.country);
    }

    const location = place?.geometry?.location;
    if (location && typeof location.lat === 'function' && typeof location.lng === 'function') {
      this.setField('latitude', location.lat());
      this.setField('longitude', location.lng());
    }

    const el = this.elRef.nativeElement;
    if (parsed.address1 && el.value !== parsed.address1) {
      this.applying = true;
      el.value = parsed.address1;
      el.dispatchEvent(new Event('input', { bubbles: true }));
      this.applying = false;
    }
  }

  private setField(field: string, value: string | number) {
    if (!field || value === undefined || value === null || value === '') return;
    if (typeof this.model.get === 'function') {
      const control = this.model.get(field);
      if (control) {
        control.setValue(value);
        control.markAsDirty();
        control.markAsTouched();
      }
      return;
    }
    this.model[field] = value;
  }
}
